import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  FileText, Upload, Download, Trash2, Eye, Search,
  File, FileImage, FileSpreadsheet, Filter, Plus,
  FolderOpen, Clock, CheckCircle, Package, Users
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'rate_con', label: 'Rate Confirmation', color: 'bg-blue-500' },
  { value: 'bol', label: 'Bill of Lading', color: 'bg-green-500' },
  { value: 'pod', label: 'Proof of Delivery', color: 'bg-purple-500' },
  { value: 'invoice', label: 'Invoice', color: 'bg-orange-500' },
  { value: 'insurance', label: 'Insurance', color: 'bg-red-500' },
  { value: 'contract', label: 'Contract', color: 'bg-cyan-500' },
  { value: 'other', label: 'Other', color: 'bg-slate-500' }
];

const getDocTypeConfig = (type) => {
  return DOCUMENT_TYPES.find(t => t.value === type) || DOCUMENT_TYPES[DOCUMENT_TYPES.length - 1];
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (fileType) => {
  if (['.pdf'].includes(fileType)) return <FileText className="w-5 h-5 text-red-400" />;
  if (['.png', '.jpg', '.jpeg'].includes(fileType)) return <FileImage className="w-5 h-5 text-blue-400" />;
  if (['.xls', '.xlsx'].includes(fileType)) return <FileSpreadsheet className="w-5 h-5 text-green-400" />;
  return <File className="w-5 h-5 text-gray-400" />;
};

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);
  
  // Upload modal
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    document_type: 'other',
    load_id: '',
    notes: ''
  });

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType) params.append('document_type', filterType);
      
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/documents?${params.toString()}`),
        axios.get(`${API}/documents/stats/summary`)
      ]);
      
      setDocuments(docsRes.data.documents || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      setUploadForm(prev => ({ ...prev, title: file.name }));
      setUploadOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadForm.title || uploadFile.name);
      formData.append('document_type', uploadForm.document_type);
      if (uploadForm.load_id) formData.append('load_id', uploadForm.load_id);
      
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded successfully!');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadForm({ title: '', document_type: 'other', load_id: '', notes: '' });
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"?`)) return;
    
    try {
      await axios.delete(`${API}/documents/${doc.id}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const filteredDocs = documents.filter(doc => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return doc.title?.toLowerCase().includes(q) || 
           doc.original_filename?.toLowerCase().includes(q) ||
           doc.document_type?.toLowerCase().includes(q);
  });

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6" data-testid="documents-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <FolderOpen className="w-7 h-7 text-blue-500" />
              Document Management
            </h1>
            <p className="text-gray-400 mt-1">Rate Cons, BOLs, PODs, Invoices & more</p>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
              onChange={handleFileSelect}
              data-testid="file-input"
            />
            <Button 
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="upload-btn"
            >
              <Upload className="w-4 h-4" /> Upload Document
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-white">{stats.total_documents}</p>
                <p className="text-xs text-gray-400">Total</p>
              </CardContent>
            </Card>
            {DOCUMENT_TYPES.slice(0, 6).map(type => (
              <Card 
                key={type.value} 
                className={`bg-slate-800/50 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ${filterType === type.value ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setFilterType(filterType === type.value ? '' : type.value)}
              >
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold text-white">{stats.by_type?.[type.value] || 0}</p>
                  <p className="text-xs text-gray-400">{type.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search documents..."
                    className="pl-10 bg-slate-700/50 border-slate-600"
                    data-testid="search-input"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white text-sm"
                  data-testid="filter-select"
                >
                  <option value="">All Types</option>
                  {DOCUMENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              {filterType && (
                <Button variant="outline" size="sm" onClick={() => setFilterType('')}>
                  Clear Filter
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documents Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="text-gray-400 mt-4">Loading documents...</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <FolderOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Documents Yet</h3>
              <p className="text-gray-400 mb-4">Upload your first document to get started</p>
              <Button onClick={() => fileInputRef.current?.click()} className="bg-blue-600 hover:bg-blue-700">
                <Upload className="w-4 h-4 mr-2" /> Upload Document
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredDocs.map(doc => {
              const typeConfig = getDocTypeConfig(doc.document_type);
              return (
                <Card 
                  key={doc.id} 
                  className="bg-slate-800/50 border-slate-700 hover:border-slate-500 transition-colors"
                  data-testid={`doc-card-${doc.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.file_type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate" title={doc.title}>
                            {doc.title}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{doc.original_filename}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full text-white ${typeConfig.color}`}>
                        {typeConfig.label}
                      </span>
                      <span className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</span>
                    </div>
                    
                    {doc.load_id && (
                      <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                        <Package className="w-3 h-3" />
                        <span>Load: {doc.load_id.substring(0, 8)}...</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                      <Clock className="w-3 h-3" />
                      <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1 gap-1"
                        onClick={() => handleDownload(doc)}
                        data-testid={`download-btn-${doc.id}`}
                      >
                        <Download className="w-3 h-3" /> Download
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={() => handleDelete(doc)}
                        data-testid={`delete-btn-${doc.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-500" />
                Upload Document
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-2">
              {uploadFile && (
                <div className="p-3 bg-slate-700/50 rounded-lg flex items-center gap-3">
                  {getFileIcon(`.${uploadFile.name.split('.').pop()}`)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{uploadFile.name}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(uploadFile.size)}</p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => { setUploadFile(null); fileInputRef.current.value = ''; }}
                  >
                    Change
                  </Button>
                </div>
              )}
              
              <div>
                <Label className="text-gray-300">Document Title</Label>
                <Input
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  placeholder="Enter title"
                  className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                  data-testid="upload-title-input"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Document Type</Label>
                <select
                  value={uploadForm.document_type}
                  onChange={(e) => setUploadForm({...uploadForm, document_type: e.target.value})}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white"
                  data-testid="upload-type-select"
                >
                  {DOCUMENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-gray-300">Link to Load ID (optional)</Label>
                <Input
                  value={uploadForm.load_id}
                  onChange={(e) => setUploadForm({...uploadForm, load_id: e.target.value})}
                  placeholder="Enter Load ID to link"
                  className="mt-1 bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              
              <Button 
                onClick={handleUpload}
                disabled={uploading || !uploadFile}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-upload-btn"
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DocumentsPage;
